/**
 * Shared props for numeric `<Input type="number" />` fields across the
 * reservation forms. Solves two long-standing issues at once:
 *
 * 1. Users clicking the field saw the digits stay in place — typing then
 *    appended next to the old value ("0" + "5" → "05"). Root cause: calling
 *    `.select()` inside `onFocus` gets *undone* by the browser's default
 *    mouseup handler that positions the caret. We now defer the select
 *    with `requestAnimationFrame` so it lands **after** mouseup.
 *
 * 2. Some browsers (Safari especially) don't support `.select()` on
 *    `type="number"` reliably. We temporarily swap `type=text` around the
 *    `setSelectionRange` call to force a real selection, then restore.
 *
 * 3. If the field currently reads "0", we clear it once selection is set
 *    so typing feels instant (no leading zero to backspace).
 *
 * Import as: `import { numberFieldProps } from "@/lib/numberInput";`
 */

function selectAllDeferred(el) {
  // Run *after* the click's mouseup event so our selection sticks.
  const doSelect = () => {
    if (!el || document.activeElement !== el) return;
    try {
      // setSelectionRange works reliably on text inputs; swap the type
      // temporarily for number inputs so the API accepts our range call.
      const wasNumber = el.type === "number";
      if (wasNumber) el.type = "text";
      const len = String(el.value ?? "").length;
      el.setSelectionRange(0, len);
      if (wasNumber) el.type = "number";
    } catch {
      // Fallback for oddball browsers where select() still works.
      try { el.select(); } catch { /* give up silently */ }
    }
  };
  // rAF > setTimeout(0) here — Chrome/Safari fire mouseup between the
  // synchronous focus handler and the next paint, so rAF gets us in *after*
  // the browser has finished positioning its own caret.
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(doSelect);
  else setTimeout(doSelect, 0);
}

export const numberFieldProps = {
  onFocus: (e) => selectAllDeferred(e.currentTarget),
  onMouseUp: (e) => {
    // The default mouseup collapses the selection we just made in onFocus.
    // Only prevent this on the *first* click that focuses the field — once
    // it already had focus, allow normal caret positioning.
    if (e.currentTarget.dataset.__nfpJustFocused === "1") {
      e.preventDefault();
      delete e.currentTarget.dataset.__nfpJustFocused;
    }
  },
  onMouseDown: (e) => {
    if (document.activeElement !== e.currentTarget) {
      // Mark that the next mouseup should be intercepted.
      e.currentTarget.dataset.__nfpJustFocused = "1";
    }
  },
};

export const dateFieldProps = {
  onClick: (e) => { try { e.currentTarget.showPicker?.(); } catch { /* not supported */ } },
  onFocus: (e) => { try { e.currentTarget.showPicker?.(); } catch { /* not supported */ } },
};
